import {
  call,
  put,
  takeLatest,
  select,
  take,
  fork,
  cancel,
} from 'redux-saga/effects';
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import rsf from '../../helpers/firebase';
import types from './actionTypes';
import * as affiliatesActions from './actions';
import { actualUser } from '../../selectors/user';
import { isAdmin as isAdminSelector } from '../../selectors/auth';
import { toDateFirebase } from '../../helpers/sharedFunction';

const db = getFirestore(rsf.app);

function* createAffiliateSaga(action) {
  try {
    const user = yield select(actualUser);
    const affiliateData = {
      ...action.affiliate,
      createdBy: user.id,
      createdAt: Timestamp.now(),
      isActive: true,
    };

    const docRef = yield call(
      addDoc,
      collection(db, 'affiliates'),
      affiliateData,
    );
    const affiliate = { id: docRef.id, ...affiliateData };

    yield put(affiliatesActions.createAffiliateSuccess(affiliate));
  } catch (error) {
    console.error('Error creating affiliate:', error);
    yield put(affiliatesActions.createAffiliateFailure(error.message));
  }
}

// Update affiliate
function* updateAffiliateSaga(action) {
  try {
    const { affiliateId, updates } = action;
    const user = yield select(actualUser);

    // Check if user has permission to update this affiliate
    const isAdmin = yield select(isAdminSelector);
    if (!isAdmin) {
      const affiliate = yield select((state) =>
        state.Affiliate.affiliates.find((a) => a.id === affiliateId),
      );
      if (!affiliate || affiliate.createdBy !== user.id) {
        throw new Error('Unauthorized to update this affiliate');
      }
    }

    const affiliateRef = doc(db, 'affiliates', affiliateId);
    yield call(updateDoc, affiliateRef, {
      ...updates,
      updatedAt: Timestamp.now(),
    });

    const updatedAffiliate = { id: affiliateId, ...updates };
    yield put(affiliatesActions.updateAffiliateSuccess(updatedAffiliate));
  } catch (error) {
    console.error('Error updating affiliate:', error);
    yield put(affiliatesActions.updateAffiliateFailure(error.message));
  }
}

const transformAffiliate = (docs) => {
  const affiliates = [];

  docs.forEach((affiliate) => {
    const data = affiliate.data();
    affiliates.push({
      id: affiliate.id,
      ...data,
      createdAt: toDateFirebase(affiliate, data).toDate(),
      ...(data.updatedAt && {
        updatedAt: toDateFirebase(affiliate, data, 'updatedAt').toDate(),
      }),
    });
  });

  return affiliates;
};

function* syncAffiliatesSaga() {
  const affiliatesRef = query(
    collection(db, 'affiliates'),
    orderBy('createdAt', 'desc'),
  );

  const task = yield fork(rsf.firestore.syncCollection, affiliatesRef, {
    successActionCreator: affiliatesActions.syncAffiliatesSuccess,
    failureActionCreator: affiliatesActions.syncAffiliatesFailure,
    transform: (docs) => transformAffiliate(docs),
  });

  yield take(types.RESET_AFFILIATE_STATE);
  yield cancel(task);
}

export default function* affiliateSaga() {
  yield takeLatest(types.SYNC_AFFILIATES.REQUEST, syncAffiliatesSaga);
  yield takeLatest(types.CREATE_AFFILIATE.REQUEST, createAffiliateSaga);
  yield takeLatest(types.UPDATE_AFFILIATE.REQUEST, updateAffiliateSaga);
}
